import {
  getAuth,
  multiFactor,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  type User as FirebaseUser,
} from 'firebase/auth';
import type { AuthProvider, User, SessionChange, AccessContext, AccessRequirement } from '../AuthProvider';
import { useLocalStorage } from '@vueuse/core';

export class FirebaseAuth implements AuthProvider {
  private mfaEnabled: boolean = true;

  init(onSessionChange: SessionChange): Promise<() => void> {
    return new Promise((resolve, reject) => {
      let initialized = false;
      let unsubscribe: () => void = () => {};

      unsubscribe = onAuthStateChanged(
        getAuth(),
        (user) => {
          onSessionChange(user ? this.mapUser(user) : null);
          if (!initialized) {
            initialized = true;
            resolve(unsubscribe);
          }
        },
        reject,
      );
    });
  }

  async getCurrentUser(): Promise<User | null> {
    const auth = getAuth();
    await auth.authStateReady();
    return auth.currentUser ? this.mapUser(auth.currentUser) : null;
  }

  async getToken(): Promise<string | null> {
    const auth = getAuth();
    await auth.authStateReady();
    return auth.currentUser?.getIdToken() ?? null;
  }

  async login(email: string, password: string): Promise<User> {
    try {
      const credential = await signInWithEmailAndPassword(getAuth(), email, password);
      return this.mapUser(credential.user);
    } catch (error: any) {
      if (error.code === 'auth/invalid-credential') {
        throw new Error('auth/invalid-credential');
      }
      throw error;
    }
  }

  async logout(): Promise<void> {
    await getAuth().signOut();
  }

  async checkAccess(context: AccessContext): Promise<AccessRequirement | null> {
    const user = getAuth().currentUser;
    if (!user) throw new Error('Firebase session is unavailable');

    if (!user.emailVerified) return 'verify-email';

    if (
      !context.fromAuthScreen ||
      context.passwordResetRecommended === undefined ||
      !user.providerData.some((provider) => provider.providerId === 'password')
    ) {
      return null;
    }

    if (context.passwordResetRecommended) return 'reset-password';

    const mfaRequirement = await this.checkMFA();
    if (mfaRequirement) {
      return mfaRequirement;
    }

    return null;
  }

  async checkMFA(): Promise<'enroll-mfa' | 'submit-mfa' | null> {
    if (!this.hasMFAEnabled()) return null;

    const user = getAuth().currentUser;
    if (!user) throw new Error('Firebase session is unavailable');

    const skipMfa = useLocalStorage(`skip-enroll-mfa-${user.uid}`, false);
    if (this.hasMFAEnabled() && multiFactor(user).enrolledFactors.length === 0 && !skipMfa.value) {
      return 'enroll-mfa';
    }

    return null;
  }

  hasMFAEnabled(): boolean {
    return this.mfaEnabled;
  }

  private mapUser(user: FirebaseUser): User {
    return { id: user.uid, email: user.email };
  }
}
